import "styles/signUpForm.less";
import { REGION } from "constants/common";
import { CLIENT_CREATED_ACCOUNT } from "constants/events";
import { InputType, UK_SIGNUP_STEP } from "constants/signupForm";
import { getCurrentTaxYear } from "constants/taxYears";

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { Form, message } from "antd";
import { AppContext } from "App";
import backArrow from "assets/images/arrow-back-grey.svg";
import nextArrow from "assets/images/left-arrow.svg";
import FormItemWrapper from "components/basic/FormWrapper/FormItemWrapper";
import LottieAnimation from "components/basic/Spinner/Lottie";
import FloatInput from "components/inputs/Form/Inputs/FloatInput";
import { CategoryTitle } from "enums/questionnaire";
import { useAppSize } from "hooks/helpers/useAppSize";
import useDebounce from "hooks/helpers/useDebounce";
import { useDeviceData } from "react-device-detect";
import ReactMetaPixel from "react-facebook-pixel";
import { useLocation, useNavigate } from "react-router-dom";
import ReactSnapchatPixel from "react-snapchat-pixel";
// @ts-ignore
import TwitterPixel from "react-twitter-pixel";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { login } from "redux/reducers/auth";
import { setCurrentDocumentYear } from "redux/reducers/document";
import { setCompletedCategories, setInProgressCategories, setNavigationItems } from "redux/reducers/navigation";
import { RootState } from "redux/store";
import { useSignInMutation } from "services/auth";
import { checkClientEmailAvailability, useCreateClientMutation } from "services/client";
import {
    useCreatePotentialClientMutation,
    useUpdatePotentialClientByIdMutation,
} from "services/potential-clients";
import { IUtmParameters } from "types/potential-clients";
import { DependT } from "types/questionnaire";
import sendGTMEvent from "utils/gtm-utils";
import sendGaEvent from "utils/react-ga";
import { logError } from "utils/sentry";
import {
    isEmailValid,
    isNameValid,
    isProbableEmailDomainMisspelling,
} from "utils/validation";

import SignInputs from "./SignInputs";

export type ErrorsT = {
    [key: string]: string;
};
export type WarningsT = {
    [key: string]: string;
};
export type FormDataT = {
    [key: string]: any;
};
export type EnableValidationT = {
    [key: string]: boolean;
};

const handleAnswerMatch = (
    depend: DependT,
    answers: Record<string, any> | undefined
): boolean => {
    if (!answers) return false;

    const answer = answers[depend.id];

    if (Array.isArray(answer)) {
        return answer.includes(depend.value);
    }

    return answer === depend.value;
};

export const handleOpposite = (
    depend: DependT,
    answers: Record<string, any>
): boolean => {
    const answer = answers[depend.id];
    if (Array.isArray(answer)) {
        return !answer.includes(depend.value);
    }
    return answer && answer !== depend.value;
};

export const checkDepend = (
    depends: DependT[],
    answers?: Record<string, any>
) => {
    if (!depends?.length || !answers) return true;
    const orDepends = depends.filter(depend => depend.orDepend);
    const andDepends = depends.filter(depend => !depend.orDepend);

    const isDependMatch = (depend: DependT): boolean => {
        if (depend?.opposite && answers) {
            return handleOpposite(depend, answers);
        }

        return handleAnswerMatch(depend, answers);
    };

    const andDependsMatch = andDepends.every(isDependMatch);
    if (andDependsMatch) {
        return true;
    }

    if (orDepends.length) {
        return orDepends.some(isDependMatch);
    }
    return false;
};

export type SignDataType = {
    termsAgreed: boolean;
    signature: Blob | null;
    referralCode: string;
    password: string;
};

const SignUpForm = ({ step1VariableInfo }: {
    step1VariableInfo?: {
        headerLabel: string,
        nextButtonLabel: string,
        infoText: string
    }
}) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const isMobile = useAppSize();
    const deviceData = useDeviceData(window.navigator.userAgent);
    const { authData } = useContext(AppContext);
    const scrollRef = useRef<HTMLDivElement>(null);
    const prevScrollHeightRef = useRef(0);
    const hasFinalizedRef = useRef(false);

    const [createClient, { isLoading: isCreatingClient }] = useCreateClientMutation();
    const [signIn, { isLoading: isSigningIn }] = useSignInMutation();
    const [updatePotentialClientById] = useUpdatePotentialClientByIdMutation();
    const [createPotentialClient] = useCreatePotentialClientMutation();
    const { taxYears } = useAppSelector((state: RootState) => ({
        taxYears: state.taxYears.taxYears,
    }));
    const [currentStep, setCurrentStep] = useState(UK_SIGNUP_STEP.PERSONAL_INFO);
    const [inputData, setInputData] = useState<FormDataT>({});
    const [errors, setErrors] = useState<ErrorsT>({});
    const [warnings, setWarnings] = useState<WarningsT>({});
    const [enableValidation, setEnableValidation] = useState<EnableValidationT>({});
    const [isCreateAccountChecked, setIsCreateAccountChecked] = useState(true);
    const [passwordError, setPasswordError] = useState<boolean>(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [authReady, setAuthReady] = useState<boolean>(false);
    const [signData, setSignData] = useState<SignDataType>({
        termsAgreed: true,
        signature: null,
        referralCode: "",
        password: "",
    });

    const ukSignupData = useMemo(() => [
        {
            id: UK_SIGNUP_STEP.PERSONAL_INFO,
            headerLabel: step1VariableInfo && step1VariableInfo.headerLabel ? step1VariableInfo.headerLabel : "Start Your Return",
            nextButtonLabel:  step1VariableInfo && step1VariableInfo.nextButtonLabel ? step1VariableInfo.nextButtonLabel : "START TAX RETURN",
            account: "Already have an account? Login",
            backButtonId: null,
            nextButtonId: UK_SIGNUP_STEP.SIGN,
            infoText:
                step1VariableInfo && step1VariableInfo.infoText ? step1VariableInfo.infoText : "Enter your details to begin your tax return.",
            inputs: [
                {
                    id: "firstName",
                    depend: [],
                    type: "nameQuestion",
                    name: "First Name",
                    inputName: "given-name",
                    required: true,
                    placeholder: "Enter your first name",
                },
                {
                    id: "lastName",
                    depend: [],
                    type: "nameQuestion",
                    name: "Last Name",
                    inputName: "family-name",
                    required: true,
                    placeholder: "Enter your last name",
                },
                {
                    id: "email",
                    depend: [],
                    type: "emailQuestion",
                    name: "Email Address",
                    inputName: "email",
                    required: true,
                    placeholder: "Enter your email address",
                },
            ],
        },
        {
            id: UK_SIGNUP_STEP.SIGN,
            headerLabel: "Final Step",
            nextButtonLabel: "SIGN & SUBMIT",
            account: "",
            backButtonId: UK_SIGNUP_STEP.PERSONAL_INFO,
            nextButtonId: null,
            infoText: "Enter password to create an account.",
            inputs: [
                {
                    id: "sign",
                    depend: [],
                    type: "signQuestion",
                    name: "Create password",
                    inputName: "new-password",
                    required: true,
                    placeholder: "Create a password",
                },
            ],
        },
    ], [step1VariableInfo]);

    // removing leading and trailing spaces
    if (inputData["email"]) {
        inputData["email"] = inputData["email"].trim();
    }
    const emailToCheck = inputData["email"];
    const debouncedEmailToValidate = useDebounce(emailToCheck, 1000);
    const isValidEmail = useMemo(() => isEmailValid(emailToCheck), [emailToCheck]);
    const { data: emailAvailability } =
        checkClientEmailAvailability.useQuery(
            { email: emailToCheck },
            { skip: !isValidEmail, refetchOnMountOrArgChange: true }
        );

    const currentStepData = useMemo(
        () => ukSignupData.find(step => step.id === currentStep),
        [currentStep, ukSignupData]
    );

    const scrollContentToTop = useCallback(() => {
        if (scrollRef.current) {
            const scrollTop = 0; // Scroll to the top
            if (scrollTop !== prevScrollHeightRef.current) {
                scrollRef.current.scrollTo({
                    top: scrollTop, // Scroll to the top position (0)
                    behavior: "smooth", // Smooth scrolling
                });
                prevScrollHeightRef.current = scrollTop;
            }
        }
    }, []);

    const scrollContentToBottom = useCallback(() => {
        if (scrollRef.current) {
            const scrollHeight = scrollRef.current.scrollHeight;
            if (scrollHeight !== prevScrollHeightRef.current) {
                scrollRef.current.scrollTo({
                    top: scrollHeight,
                    behavior: "smooth",
                });
                prevScrollHeightRef.current = scrollHeight;
            }
        }
    }, []);

    const updatePotentialClient = useCallback(() => {
        const utmParametersString = localStorage.getItem("utmParamData");
        const potentialClientId = localStorage.getItem("potentialClientId");
        const utmParameters: IUtmParameters | null = utmParametersString ? JSON.parse(utmParametersString) : null;
        if (potentialClientId) {
            updatePotentialClientById({
                potentialClientId,
                tab: currentStepData?.id || "",
                email: inputData["email"],
                firstName: inputData["firstName"],
                lastName: inputData["lastName"],
                ...(utmParameters && { utmParameters: utmParameters }),
            });
            sendGTMEvent({
                event: "startForm",
                user_data: {
                    email_address: inputData["email"],
                },
            });
        }
    }, [currentStepData?.id, inputData, updatePotentialClientById]);

    const handleInputChange = useCallback(
        ({ name, value, object }: { name?: string, value?: string | string[], object?: { [key: string]: string } }) => {
            if (name) {
                setEnableValidation((prev: EnableValidationT) => ({
                    ...prev,
                    [name]: true,
                }));
                setInputData((prev: FormDataT) => ({ ...prev, ...object, [name]: value }));
            } else if (object) {
                setInputData((prev: FormDataT) => ({ ...prev, ...object }));
            } else {
                throw new Error("Name or object are required");
            }
        },
        []
    );

    const validate = useCallback(() => {
        const newErrors: Record<string, any> = {};
        const newWarnings: Record<string, any> = {};

        newErrors["email"] = errors["email"];
        newWarnings["email"] = warnings["email"];

        currentStepData?.inputs.forEach(input => {
            if (enableValidation[input.id]) {
                const inputValue = inputData[input.id];
                if (input.type === InputType.SIGN_QUESTION) {
                    if (isCreateAccountChecked) {
                        newErrors[input.id] =
                            !signData.termsAgreed ||
                            !signData.password ||
                            !!passwordError
                                ? "sign data required"
                                : "";
                    } else {
                        newErrors[input.id] =
                            !signData.termsAgreed
                                ? "sign data required"
                                : "";
                    }
                }
                if (input.type === InputType.NAME_QUESTION) {
                    const isValid = isNameValid(inputValue);
                    if (!isValid) {
                        newErrors[input.id] =
                            "Please write your name without an apostrophe";
                    }
                    if (inputValue && inputValue.length < 2) {
                        newErrors[input.id] =
                            "Name must be at least 2 characters long";
                    }
                }
                if (input.required && !inputValue) {
                    newErrors[input.id] = "This field is required";
                }
            }
        });
        setErrors(newErrors);
        setWarnings(newWarnings);
        return { errors: newErrors, warnings: newWarnings };
    }, [currentStepData?.inputs, enableValidation, errors, inputData, isCreateAccountChecked, passwordError, signData.password, signData.termsAgreed, warnings]);

    const finalizeClentCreation = useCallback(async () => {
        dispatch(setNavigationItems([
            CategoryTitle.BankAccountConnection,
            CategoryTitle.PersonalInfo,
            CategoryTitle.General,
            CategoryTitle.ReviewAndSubmit,
        ]));
        dispatch(setCompletedCategories([CategoryTitle.BankAccountConnection]));
        dispatch(setInProgressCategories([CategoryTitle.PersonalInfo]));
        dispatch(setCurrentDocumentYear(getCurrentTaxYear(taxYears)));
        navigate("/onboarding");
    }, [dispatch, navigate, taxYears]);

    const handleSignDataChange = (data: SignDataType) => {
        setSignData(data);
        if (data.termsAgreed) {
            setInputData((prev: FormDataT) => ({ ...prev, sign: "provided" }));
        }
    };

    const handleNextClick = useCallback(async () => {
        if (currentStepData?.nextButtonId) {
            const nextStepId = currentStepData.nextButtonId;
            setCurrentStep(nextStepId);
            const searchParams = new URLSearchParams(location.search);
            searchParams.set("step", nextStepId);
            navigate(`${location.pathname}?${searchParams.toString()}`);
            return;
        }

        if (!signData.termsAgreed) return;

        try {
            const formData = new FormData();
            formData.append("region", REGION.UK);
            formData.append("email", inputData["email"]);
            formData.append("firstName", inputData["firstName"]);
            formData.append("lastName", inputData["lastName"]);
            formData.append("password", signData.password);
            formData.append("deviceDetect", JSON.stringify(deviceData));

            await createClient(formData).unwrap();

            sendGaEvent(CLIENT_CREATED_ACCOUNT);
            sendGTMEvent({
                event: "SubmitForm",
                user_data: { email_address: inputData["email"] },
            });

            process.env.REACT_APP_META_PIXEL_ID && ReactMetaPixel.fbq("track", "SubmitApplication");

            if (process.env.REACT_APP_TWITTER_PIXEL_ID && process.env.REACT_APP_TWITTER_SUBMIT_EVENT_CODE) {
                TwitterPixel.twq("event", process.env.REACT_APP_TWITTER_SUBMIT_EVENT_CODE, {
                    conversion_id: null,
                    email_address: inputData["email"],
                });
            }

            process.env.REACT_APP_SNAPCHAT_PIXEL_ID && ReactSnapchatPixel.track("SIGN_UP");

            if (signData.password) {
                const data = await signIn({
                    email: inputData["email"],
                    password: signData.password,
                }).unwrap();

                await dispatch(login(data.token)).unwrap();
                setAuthReady(true);
            }
        } catch (error: any) {
            const formDataObject = {
                email: inputData["email"],
                firstName: inputData["firstName"],
                lastName: inputData["lastName"],
                region: REGION.UK,
            };

            logError(error, {
                function: "handleNextClick",
                extra: formDataObject,
                potentialClientId: localStorage.getItem("potentialClientId"),
            });

            await message.error(
                error?.data?.message ??
                "An error occurred. Please try again."
            );
        } finally {
            updatePotentialClient();
        }
    }, [
        currentStepData,
        inputData,
        signData,
        deviceData,
        createClient,
        signIn,
        navigate,
        location,
        updatePotentialClient,
        dispatch,
        setAuthReady,
    ]);

    const handleBackClick = useCallback(() => {
        scrollContentToTop();

        if (currentStepData?.backButtonId) {
            setCurrentStep(currentStepData?.backButtonId);
            const searchParams = new URLSearchParams(location.search);
            searchParams.set("step", currentStepData?.backButtonId);
            navigate(`${location.pathname}?${searchParams.toString()}`);
        }
    }, [scrollContentToTop, currentStepData, setCurrentStep, location, navigate]);

    const handleKeyDown = (event: any) => {
        if (event.key === "Enter") {
            if (isMobile) {
                event.target.blur();
            }
        }
    };

    useEffect(() => {
        const { errors } = validate();

        const currentStepErrors = currentStepData?.inputs.filter(
            input => {
                const showQuestion = checkDepend(input.depend, inputData);
                // @ts-ignore
                return showQuestion ? errors[input.id] : undefined;
            }
        ).length;

        const notAnsweredRequiredQuestions = currentStepData?.inputs.filter(
            input => {
                const showQuestion = checkDepend(input.depend, inputData);
                return (
                    input.required && showQuestion && !inputData[input.id]
                );
            }
        );

        setIsButtonDisabled(
            !!notAnsweredRequiredQuestions?.length || !!currentStepErrors
        );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputData, enableValidation, currentStepData, signData, currentStep]);

    useEffect(() => {
        const newErrors: Record<string, any> = { ...errors };
        const newWarnings: Record<string, any> = { ...warnings };
        newErrors["email"] = emailAvailability?.available === false ? "This email address already exists" : "";
        newWarnings["email"] = "";

        setErrors(newErrors);
        setWarnings(newWarnings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emailToCheck, emailAvailability]);

    useEffect(() => {
        const newErrors: Record<string, any> = { ...errors };
        const newWarnings: Record<string, any> = { ...warnings };
        const isValid = isEmailValid(debouncedEmailToValidate);
        const isProbableMistakeEmailDomain = isProbableEmailDomainMisspelling(debouncedEmailToValidate);
        if (debouncedEmailToValidate && !isValid) {
            newErrors["email"] = "Please enter a valid email address";
        } else if (isProbableMistakeEmailDomain) {
            newWarnings["email"] = "This email address looks unusual. Proceed anyway if you're sure it's correct!";
        }

        setErrors(newErrors);
        setWarnings(newWarnings);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedEmailToValidate]);

    useEffect(() => {
        scrollContentToTop();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStepData]);

    useEffect(() => {
        scrollContentToBottom();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputData]);

    useEffect(() => {
        setCurrentStep(UK_SIGNUP_STEP.PERSONAL_INFO);
        const potentialClientId = localStorage.getItem("potentialClientId");
        const utmParametersString = localStorage.getItem("utmParamData");
        const utmParameters: IUtmParameters | null = utmParametersString ? JSON.parse(utmParametersString) : null;

        if (!potentialClientId) {
            createPotentialClient({
                ...(utmParameters && { utmParameters: utmParameters }),
            }).unwrap()
                .then(data => {
                    localStorage.setItem("potentialClientId", data.data.potentialClient._id);
                })
                .catch((error: any) => {
                    const contextInfo = {
                        potentialClientId,
                        function: "createPotentialClient",
                        error: error.message,
                    };
                    logError(error, contextInfo);
                });
        }
        return () => {
            // Cleanup function to reset state or perform any necessary cleanup
            setInputData({});
            setEnableValidation({});
            setCurrentStep(UK_SIGNUP_STEP.PERSONAL_INFO);
            localStorage.removeItem("potentialClientId");
            localStorage.removeItem("utmParamData");
            setSignData({
                termsAgreed: true,
                signature: null,
                referralCode: "",
                password: "",
            });
            setErrors({});
            setWarnings({});
            setIsCreateAccountChecked(true);
            setPasswordError(false);
            setIsButtonDisabled(true);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (authReady && authData && !hasFinalizedRef.current) {
            hasFinalizedRef.current = true;
            finalizeClentCreation();
        }
    }, [authReady, authData, finalizeClentCreation]);

    useEffect(() => {
        if (location.state?.email) {
            setInputData((prev: FormDataT) => ({
                ...prev,
                email: location.state.email,
            }));
        }
    }, [location.state]);

    return (
        <div>
            <LottieAnimation isLoading={isCreatingClient || isSigningIn}/>
            <div id="sign-up" className="sign-up-form__header-wrapper">
                <div className="sign-up-form__header ">
                    <div className={"sign-up-form__header-title"}
                    >
                        {currentStepData?.headerLabel}
                    </div>
                    {}
                    <div>
                        {currentStepData?.backButtonId && (
                            <div
                                className="dflex sign-up-form__back-button"
                                onClick={handleBackClick}
                            >
                            BACK{" "}
                                <img
                                    src={backArrow}
                                    width={11}
                                    height={11}
                                    alt={"back button"}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="sign-up-form__content" ref={scrollRef}>
                {currentStepData?.infoText && (
                    <span
                        className={"sign-up-form__help-text"}
                    >
                        {currentStepData?.infoText}
                    </span>
                )}
                <Form data-testid="sign-up-form" layout={"vertical"}>
                    {currentStepData?.inputs.map((input, index) => {
                        const showQuestion = checkDepend(
                            input.depend,
                            inputData
                        );
                        if (!showQuestion) return null;

                        switch (input.type) {
                        case InputType.SIGN_QUESTION:
                            return (
                                <div key={input.id}>
                                    <SignInputs
                                        setSignData={handleSignDataChange}
                                        signData={signData}
                                        isCreateAccountChecked={
                                            isCreateAccountChecked
                                        }
                                        setIsCreateAccountChecked={
                                            setIsCreateAccountChecked
                                        }
                                        passwordError={passwordError}
                                        setPasswordError={setPasswordError}
                                        inputId={input.id}
                                        handleKeyDown={handleKeyDown}
                                    />
                                </div>
                            );
                        case InputType.NAME_QUESTION:
                        case InputType.EMAIL_QUESTION:
                        default:
                            return (
                                <FormItemWrapper
                                    key={input.id}
                                    error={errors[input.id]}
                                    warning={warnings[input.id]}
                                    label={input.name}
                                    required={input.required}
                                >
                                    <FloatInput
                                        required={input.required}
                                        placeholder={input.placeholder}
                                        index={
                                            currentStepData.id !==
                                        UK_SIGNUP_STEP.PERSONAL_INFO
                                                ? index
                                                : undefined
                                        }
                                        value={inputData[input.id]}
                                        error={errors[input.id]}
                                        onChange={(event: any) => {
                                            handleInputChange({
                                                name: input.id,
                                                value: event.target.value,
                                            });
                                        }}
                                        type={input.type === InputType.EMAIL_QUESTION ? "email" : input.type}
                                    />
                                </FormItemWrapper>
                            );
                        }
                    })}
                </Form>
            </div>
            <div className="sign-up-form__footer">
                <button
                    disabled={isButtonDisabled}
                    className={`shared__primary-button w100 ${isButtonDisabled ? "disabled" : ""}`}
                    onClick={handleNextClick}
                >
                    <span className="ml10 mAuto ">
                        {currentStepData?.nextButtonLabel}
                    </span>

                    <img
                        src={nextArrow}
                        width={11}
                        height={11}
                        alt={"next button"}
                        className="mr10"
                        style={{ transform: "rotate(180deg)" }}
                    />
                </button>
                {currentStepData?.account && <div className="mt10 pointer secondary-color taCenter" onClick={() => {
                    navigate("/auth");
                }}>
                    <span>
                        {currentStepData?.account}
                    </span>
                </div>}
            </div>
        </div>
    );
};

export default SignUpForm;
